import { buildApiUrl, getApiBaseCandidates } from "@/lib/apiBase";

export interface UserDataSnapshot {
  profile?: unknown;
  state?: {
    membership?: unknown;
    referral?: unknown;
    walletBalance?: number;
    walletHistory?: unknown[];
    paymentRecords?: unknown[];
    offlinePaymentProofs?: unknown[];
    adCredits?: number;
    favorites?: unknown[];
    serviceReviews?: unknown[];
    equipmentReviews?: unknown[];
    storefrontCustomizations?: Record<string, unknown>;
    dailyVisitProgress?: unknown;
  };
}

let preferredUserDataApiBase = "";
const unsupportedUserDataRoutes = new Set<string>();
const USER_DATA_GET_CACHE_TTL_MS = 5000;
const userDataGetRequestCache = new Map<string, { expiresAt: number; promise: Promise<Record<string, unknown>> }>();

const withAuthHeaders = (headers: HeadersInit | undefined, token: string): HeadersInit => {
  if (headers instanceof Headers) {
    const nextHeaders = new Headers(headers);
    nextHeaders.set("Authorization", `Bearer ${token}`);
    return nextHeaders;
  }

  if (Array.isArray(headers)) {
    return [...headers, ["Authorization", `Bearer ${token}`]];
  }

  return {
    ...(headers || {}),
    Authorization: `Bearer ${token}`,
  };
};

const readResponse = async <T extends Record<string, unknown>>(response: Response) => {
  let payload: Record<string, unknown> = {};

  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    throw new Error(String(payload.message || "User data request failed."));
  }

  return payload as T;
};

const userDataRequest = async <T extends Record<string, unknown>>(route: string, token: string, init?: RequestInit) => {
  const method = String(init?.method || "GET").toUpperCase();
  const isCacheableGet = method === "GET";
  const cacheKey = `${method}:${route}:${token}`;

  if (isCacheableGet) {
    const cached = userDataGetRequestCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.promise as Promise<T>;
    }
  } else {
    userDataGetRequestCache.clear();
  }

  if (unsupportedUserDataRoutes.has(route)) {
    throw new Error("User data route not found.");
  }

  const requestPromise = (async () => {
  const bases = getApiBaseCandidates(preferredUserDataApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/user-data${route}`, base), {
        ...init,
        headers: withAuthHeaders(init?.headers, token),
      });

      if (response.status === 404 && base !== bases[bases.length - 1]) {
        lastError = new Error("User data route not found.");
        continue;
      }

      if (response.status === 404) {
        unsupportedUserDataRoutes.add(route);
      }

      const payload = await readResponse<T>(response);
      preferredUserDataApiBase = base;
      return payload;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("User data request failed.");
    }
  }

  throw lastError || new Error("User data request failed.");
  })();

  if (isCacheableGet) {
    userDataGetRequestCache.set(cacheKey, {
      expiresAt: Date.now() + USER_DATA_GET_CACHE_TTL_MS,
      promise: requestPromise as Promise<Record<string, unknown>>,
    });
    requestPromise.catch(() => {
      userDataGetRequestCache.delete(cacheKey);
    });
  }

  return requestPromise;
};

export const fetchMyUserData = async (token: string) => userDataRequest<{ success: boolean; snapshot: UserDataSnapshot | null }>("/me", token);

export const fetchAdminUserProfiles = async (token: string) => {
  try {
    return await userDataRequest<{ success: boolean; profiles: Array<Record<string, unknown>>, meta?: { total?: number } }>("/profiles", token);
  } catch (error) {
    if (error instanceof Error && error.message === "User data route not found.") {
      return { success: true, profiles: [] as Array<Record<string, unknown>> };
    }

    throw error;
  }
};

export const syncUserDataSnapshot = async (token: string, userKey: string, payload: UserDataSnapshot) => userDataRequest<{ success: boolean; snapshot: UserDataSnapshot | null }>(`/${encodeURIComponent(userKey)}`, token, {
  method: "PUT",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify(payload),
});